// Черновата на листа „Инвентаризация" — общото `useWorksheetDraft` със
// снимката на инвентаризацията (формат v1, `stocktakeDraft.ts`): флагът
// „незапазени промени", autosave-ът (браузър + сървър) при нова, черновата при
// отваряне (зарежда я или пита) и нещата за футъра („Изтрий черновата" ·
// „Запази временно"). Приготвените рецепти са в `recipes` (снимката на
// `useStocktakeRecipes`) и се броят в редовете на черновата. Листът подава
// състоянието си лениво (`sheet`) — преброените редове идват от hook-а
// по-долу в тялото.
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../../store/hooks";
import type { DraftFooterProps } from "../../components/drafts/DraftWorksheetParts";
import { useWorksheetDraft } from "../../components/drafts/useWorksheetDraft";
import {
  STOCKTAKE_DRAFT_STORE,
  hasStocktakeDraftContent,
  recipePreviewItems,
  stocktakeDraftSummary,
  type StocktakeDraft,
} from "./stocktakeDraft";
import type { StocktakeRecipesSnapshot } from "./stocktakeRecipes";
import type { StocktakeRouterState } from "./useStocktakeLoad";
import { countedPreviewItems } from "./useStocktakeLeave";
import type { StocktakeComputedRow, StocktakeRowDraft } from "./stocktakeWorksheetShared";

// Състоянието на листа в момента на снимката
type SheetState = {
  date: string;
  warehouseId: number | null;
  warehouseName: string | null;
  reason: string;
  rows: Record<number, StocktakeRowDraft>;
  appliedDonorByRecipient: Map<number, number>;
  countedRows: StocktakeComputedRow[];
  // приготвените рецепти — `useStocktakeRecipes().snapshot()`
  recipes: StocktakeRecipesSnapshot;
};

type Params = {
  mode: "new" | "edit";
  homePath: string;
  routerState: StocktakeRouterState;
  sheet: () => SheetState;
  // зареди черновата в листа (без `touch`)
  apply: (payload: StocktakeDraft) => void;
  onLoadError: () => void;
};

export const useStocktakeDraft = ({ mode, homePath, routerState, sheet, apply, onLoadError }: Params) => {
  const { t } = useTranslation();
  const authNames = useAppSelector((s) => s.auth.names);
  // преброените продукти + приготвените рецепти
  const countedLinesOf = (state: SheetState) => state.countedRows.length + state.recipes.entries.length;
  const worksheetDraft = useWorksheetDraft<StocktakeDraft>({
    type: "stocktake",
    mode,
    homePath,
    routerState,
    local: STOCKTAKE_DRAFT_STORE,
    snapshot: () => {
      const state = sheet();
      return {
        payload: {
          v: 1,
          savedAt: new Date().toISOString(),
          savedBy: authNames || null,
          date: state.date,
          warehouseId: state.warehouseId,
          reason: state.reason,
          rows: state.rows,
          substitutions: [...state.appliedDonorByRecipient].map(([recipientProductId, donorProductId]) => ({
            recipientProductId,
            donorProductId,
          })),
          recipes: state.recipes,
        },
        summary: stocktakeDraftSummary(state.warehouseName, countedLinesOf(state)),
      };
    },
    resume: { hasContent: hasStocktakeDraftContent, apply, onLoadError },
  });

  // „Изтрий черновата" · „Запази временно" за футъра — вика се в рендера на
  // футъра, когато преброеното вече е сметнато
  const footer = (): DraftFooterProps => ({
    canDeleteDraft: worksheetDraft.sync.draftId != null || countedLinesOf(sheet()) > 0,
    describeDraft: () => {
      const state = sheet();
      return {
        label: state.warehouseName,
        items: [...recipePreviewItems(t, state.recipes.entries), ...countedPreviewItems(state.countedRows)],
      };
    },
    onDeleteDraft: worksheetDraft.deleteDraft,
    onSaveTemp: worksheetDraft.saveTemp,
  });

  return { ...worksheetDraft, footer };
};
